import React, { Suspense, useState } from "react";
import { Redirect } from "react-router";
import classNames from "classnames";
import * as Sentry from "@sentry/react";

import { createKeyboardShortcut } from "utils/keyboardShortcuts";

import InfoTooltip from "components/InfoTooltip";
import Loading from "components/Loading";
import ErrorFallback from "components/ErrorFallback";

import WarningIcon from "assets/images/icons/warning.svg";
import WarningOrangeIcon from "assets/images/icons/WarningOrange.svg";
import ModelPassIcon from "assets/images/icons/ModelPass.svg";

import classes from "./style.module.scss";

const INDICATOR_ICONS = {
    pass: ModelPassIcon,
    warningOrange: WarningOrangeIcon,
    warning: WarningIcon,
};

const getIndicatorIcon = (color) => {
    if (!color) return INDICATOR_ICONS.warning;
    const c = color.toLowerCase();
    if (c === "#18c1ad") return INDICATOR_ICONS.pass;
    if (c === "#f58020" || c === "#ffb22d") return INDICATOR_ICONS.warningOrange;
    return INDICATOR_ICONS.warning;
};

export default React.memo(
    ({
        children,
        tabs = [],
        tabActive,
        onChange,
        type = "",
        stretchTabs = false,
        className,
        tabClassName,
        tabNavClassName,
        tabNavProps = {},
        indicatorIconClassName,
        stack = false,
        fallbackAction = () => {},
    }) => {
        const [hoveredIndicator, setHoveredIndicator] = useState(null);

        if (!tabs.map((tab) => tab.url).includes(tabActive)) {
            return <Redirect to="/404-page-not-found" />;
        }

        const filteredChildren = children.filter((child) => typeof child === "object");

        return (
            <div
                className={classNames(classes.tabs, { [className]: className, [classes.subTabs]: type === "subTabs" })}
            >
                <div
                    className={classNames(classes.tabNav, {
                        [classes.subTabNav]: type === "subTabs",
                        [classes.stretchTabs]: stretchTabs,
                        [tabNavClassName]: tabNavClassName,
                        [classes.stack]: stack,
                    })}
                    {...tabNavProps}
                >
                    <ul>
                        {tabs.map(
                            (
                                {
                                    name,
                                    url = "",
                                    disabled,
                                    info,
                                    icon,
                                    indicator,
                                    className: tabClass,
                                    minWidth = "0rem",
                                    ...tabRest
                                },
                                index
                            ) => {
                                const activeTab = url;
                                const isActive = activeTab === tabActive;
                                const { liProps = {}, ...tabRestFiltered } = tabRest;
                                const iconNode =
                                    icon &&
                                    (typeof icon === "string" ? <img src={icon} alt={name} /> : icon);
                                const indicatorIconClass = classNames(
                                    !indicatorIconClassName && classes.tabIndicatorIcon,
                                    indicatorIconClassName
                                );
                                const indicatorNode =
                                    indicator?.node ??
                                    (indicator && !indicator.hideIcon ? (
                                        <img
                                            src={indicator.icon ?? getIndicatorIcon(indicator.color)}
                                            alt=""
                                            className={indicatorIconClass}
                                        />
                                    ) : null);
                                const renderedIndicatorNode =
                                    indicator?.node && indicatorIconClassName ? (
                                        <span className={indicatorIconClassName}>{indicatorNode}</span>
                                    ) : (
                                        indicatorNode
                                    );

                                return (
                                    <li
                                        key={index}
                                        disabled={disabled}
                                        className={classNames(classes.tabContainer, {
                                            [classes.active]: isActive,
                                            [tabClass]: tabClass,
                                        })}
                                        onClick={(e) => (!disabled ? onChange(url) : null)}
                                        style={{ minWidth: minWidth }}
                                        {...liProps}
                                        {...createKeyboardShortcut({ onKeyDown: (e) => (!disabled ? onChange(url) : null) })}
                                    >
                                        <span>
                                            {iconNode}{" "}
                                            {name}
                                            {indicator && renderedIndicatorNode && (
                                                <span
                                                    className={classes.tabIndicatorWrapper}
                                                    onMouseEnter={() => setHoveredIndicator(index)}
                                                    onMouseLeave={() => setHoveredIndicator(null)}
                                                >
                                                    {renderedIndicatorNode}
                                                </span>
                                            )}
                                            {info && <InfoTooltip info={info} className={classes.toolTip} />}
                                        </span>
                                        {indicator && hoveredIndicator === index && indicator.popoverContent && (
                                            <div className={classes.tabPopover}>
                                                {indicator.popoverContent}
                                            </div>
                                        )}
                                    </li>
                                );
                            }
                        )}
                    </ul>
                </div>
                <div
                    className={classNames(classes.tab, {
                        [classes.subTab]: type === "subTabs",
                        [tabClassName]: tabClassName,
                        [classes.stack]: stack,
                    })}
                >
                    <Sentry.ErrorBoundary
                        fallback={(error) => <ErrorFallback fallbackAction={fallbackAction} error={error} />}
                    >
                        <Suspense fallback={<Loading />}>
                            {React.Children.map(filteredChildren, (child, index) => {
                                const { url = "", name = "" } = tabs[index] || {};

                                const activeTab = url;
                                const isActive = activeTab === tabActive;

                                if (!isActive && !stack) {
                                    return null;
                                }

                                if (stack) {
                                    return (
                                        <div className={classes.stackedTab}>
                                            <h2>{name}</h2>
                                            {child}
                                        </div>
                                    );
                                }

                                return child;
                            })}
                        </Suspense>
                    </Sentry.ErrorBoundary>
                </div>
            </div>
        );
    }
);
